import React, { useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Btn, Input, Select } from "../../components/UI";
import { toast } from "sonner";
import { X, Search, Loader2, Landmark, UserPlus, Plus } from "lucide-react";
import { OpponentRow } from "./OpponentRow";

const PARTIES = ["Democratic", "Republican", "Independent", "Libertarian", "Green", "Other"];

const emptyForm = {
  candidate_name: "", party: "Democratic", office: "", state: "", district: "",
  election_date: "2026-11-03", source: "custom", fec_candidate_id: null, fec_race_id: null,
};

export const TrackCandidateModal = ({ onClose, onCreated }) => {
  const [mode, setMode] = useState("fec");
  const [form, setForm] = useState(emptyForm);
  const [opponents, setOpponents] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState(null);

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await api.get("/political-intelligence/fec-search", { params: { q: query.trim() } });
      setResults(r.data);
    } catch (e) { toast.error(errMsg(e, "Search failed")); }
    finally { setSearching(false); }
  };

  const pick = async (row) => {
    setPicked(row);
    setForm({
      candidate_name: row.display_name, party: row.party || "Other",
      office: row.office_full || "", state: row.state || "", district: row.district || "",
      election_date: "2026-11-03", source: "fec",
      fec_candidate_id: row.fec_candidate_id, fec_race_id: row.race_id,
    });
    try {
      const r = await api.get(`/political-intelligence/fec-race/${row.race_id}/opponents`, { params: { exclude: row.fec_candidate_id } });
      setOpponents(r.data.map(o => ({ name: o.name, party: o.party || "Other", fec_candidate_id: o.fec_candidate_id })));
    } catch { setOpponents([]); }
  };

  const submit = async () => {
    if (!form.candidate_name.trim()) { toast.error("Candidate name is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/political-intelligence/candidates", {
        ...form, opponents: opponents.filter(o => o.name.trim()),
      });
      toast.success(`Now tracking ${r.data.candidate_name}`);
      onCreated(r.data);
    } catch (e) { toast.error(errMsg(e, "Could not track candidate")); }
    finally { setSaving(false); }
  };

  const setOpp = (i, patch) => setOpponents(opponents.map((o, idx) => idx === i ? { ...o, ...patch } : o));

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-app border border-white/10 rounded-lg max-w-2xl w-full p-6 max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="track-candidate-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">Track a candidate</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="track-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex gap-2 mb-5">
          <button onClick={() => setMode("fec")} data-testid="track-mode-fec"
            className={`flex-1 rounded-lg border px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors ${mode === "fec" ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
            <Landmark className="w-4 h-4" /> FEC roster
          </button>
          <button onClick={() => setMode("custom")} data-testid="track-mode-custom"
            className={`flex-1 rounded-lg border px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 transition-colors ${mode === "custom" ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
            <UserPlus className="w-4 h-4" /> Custom candidate
          </button>
        </div>

        {mode === "fec" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                placeholder="Search 2,500+ declared 2026 candidates by name…" data-testid="fec-search-input"
                className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
              <Btn onClick={runSearch} disabled={searching} data-testid="fec-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
              </Btn>
            </div>
            {results && !picked && (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5" data-testid="fec-results">
                {results.length === 0 && <div className="p-4 text-sm text-slate-500">No candidates found — try a last name, or switch to Custom candidate.</div>}
                {results.map((r) => (
                  <button key={r.fec_candidate_id} onClick={() => pick(r)} data-testid={`fec-result-${r.fec_candidate_id}`}
                    className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm text-white">{r.display_name}</div>
                      <div className="text-xs text-slate-500">{r.party} · {r.office_full} · {r.state}{r.district && r.district !== "00" ? `-${r.district}` : ""}{r.incumbent_challenge ? ` · ${r.incumbent_challenge}` : ""}</div>
                    </div>
                    <span className="text-[10px] text-cyan-300">Select</span>
                  </button>
                ))}
              </div>
            )}
            {picked && (
              <div className="rounded-lg border border-cyan-400/30 bg-cyan-400/[0.05] p-4" data-testid="fec-picked-card">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-white">{form.candidate_name}</div>
                    <div className="text-xs text-slate-400">{form.party} · {form.office} · {form.state}{form.district && form.district !== "00" ? `-${form.district}` : ""}</div>
                  </div>
                  <button onClick={() => { setPicked(null); setForm(emptyForm); setOpponents([]); }}
                    className="text-xs text-slate-400 hover:text-white" data-testid="fec-change-pick">Change</button>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "custom" && (
          <div className="grid grid-cols-2 gap-3">
            <Input label="Candidate name" value={form.candidate_name} data-testid="custom-name-input"
              onChange={(e) => setForm({ ...form, candidate_name: e.target.value, source: "custom", fec_candidate_id: null, fec_race_id: null })}
              placeholder="Jane Smith" className="col-span-2" />
            <Select label="Party" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} data-testid="custom-party-select">
              {PARTIES.map(p => <option key={p}>{p}</option>)}
            </Select>
            <Input label="Office" value={form.office} onChange={(e) => setForm({ ...form, office: e.target.value })}
              placeholder="Mayor, State Senate, House…" data-testid="custom-office-input" />
            <Input label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
              placeholder="TX" maxLength={2} data-testid="custom-state-input" />
            <Input label="District (optional)" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })}
              placeholder="07" data-testid="custom-district-input" />
            <Input label="Election date" type="date" value={form.election_date}
              onChange={(e) => setForm({ ...form, election_date: e.target.value })} className="col-span-2" data-testid="custom-election-date" />
          </div>
        )}

        {(picked || mode === "custom") && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Opponents to track</div>
              <button onClick={() => setOpponents([...opponents, { name: "", party: "Other" }])} data-testid="add-opponent-btn"
                className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add opponent</button>
            </div>
            {opponents.length === 0 && <div className="text-xs text-slate-500">No opponents yet — add at least one to compute margins and momentum.</div>}
            <div className="space-y-2">
              {opponents.map((o, i) => (
                <OpponentRow
                  key={i}
                  opp={o}
                  index={i}
                  onChange={(patch) => setOpp(i, patch)}
                  onRemove={() => setOpponents(opponents.filter((_, idx) => idx !== i))}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={saving || !form.candidate_name.trim()} data-testid="track-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Start tracking
          </Btn>
        </div>
      </div>
    </div>
  );
};
